import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, RotateCcw, Volume2, VolumeX, ExternalLink } from "lucide-react";
import AnimatedCaption from "./AnimatedCaption";
import { mediaUrl } from "../lib/api";

export default function VerticalPlayer({ scenes = [], phone = true, affiliate = null, onLink, captionStyle = null, captionsEnabled = false, aspect = "9:16" }) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [prog, setProg] = useState(0);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);

  const current = scenes[index] || {};
  const useBg = captionsEnabled && !!captionStyle && !!current.bg_path;
  const aff = affiliate && affiliate.enabled ? affiliate : null;
  const cta = aff?.cta || {};
  const total = scenes.reduce((a, s) => a + (s.duration || 0), 0) || 1;
  const elapsed = scenes.slice(0, index).reduce((a, s) => a + (s.duration || 0), 0) + prog * (current.duration || 0);
  const ended = !playing && prog >= 1;
  const ctaVisible = !!aff && aff.clickable_mode !== "none" && (
    cta.start_mode === "always" ? true
      : cta.start_mode === "delay" ? elapsed >= (cta.start_time || 0)
      : elapsed >= Math.max(0, total - 5) || ended
  );
  const surfaceClickable = !!aff && aff.clickable_mode === "entire";

  const openLink = (src) => { if (onLink) onLink(src); };

  const ctaPos = { top: "top-6 left-1/2 -translate-x-1/2", center: "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2", bottom: "bottom-16 left-1/2 -translate-x-1/2" }[cta.position || "bottom"];
  const ctaSize = { sm: "text-xs px-4 py-2", md: "text-sm px-5 py-2.5", lg: "text-base px-6 py-3" }[cta.size || "md"];

  const playScene = useCallback((i) => {
    const a = audioRef.current;
    if (!a || !scenes[i]) return;
    a.src = mediaUrl(scenes[i].audio_path);
    a.muted = muted;
    a.currentTime = 0;
    a.play().catch(() => {});
  }, [scenes, muted]);

  const togglePlay = () => {
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
    } else {
      setPlaying(true);
      playScene(index);
    }
  };

  const restart = () => {
    setIndex(0);
    setProg(0);
    setPlaying(true);
    playScene(0);
  };

  const jumpTo = (i) => {
    setIndex(i);
    setProg(0);
    if (playing) playScene(i);
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => a.duration && setProg(a.currentTime / a.duration);
    const onEnd = () => {
      if (index < scenes.length - 1) {
        const next = index + 1;
        setIndex(next);
        setProg(0);
        playScene(next);
      } else {
        setPlaying(false);
        setProg(1);
      }
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, [index, scenes, playScene]);

  const Inner = (
    <div
      className="relative w-full h-full overflow-hidden rounded-[30px] bg-black cursor-pointer select-none"
      onClick={() => (surfaceClickable ? openLink("video") : togglePlay())}
      data-testid="vertical-player-stage"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="absolute inset-0"
        >
          <img
            src={mediaUrl(useBg ? current.bg_path : current.image_path)}
            alt={current.type}
            className={`absolute inset-0 w-full h-full object-cover ${playing ? "kenburns" : ""}`}
            style={{ animationDuration: `${current.duration || 5}s` }}
          />
        </motion.div>
      </AnimatePresence>

      {/* animated caption overlay (on-site preview mirrors the burned-in MP4 captions). */}
      {useBg && (
        <div className="absolute inset-0 z-10">
          <AnimatedCaption style={captionStyle} text={current.narration || current.caption} t={prog} />
        </div>
      )}

      {/* top segments */}
      <div className="absolute top-3 left-3 right-3 flex gap-1.5 z-20">
        {scenes.map((s, i) => (
          <button
            key={s.image_path || i}
            data-testid={`player-segment-${i}`}
            onClick={(e) => { e.stopPropagation(); jumpTo(i); }}
            className="h-1 flex-1 rounded-full bg-white/25 overflow-hidden"
          >
            <span
              className="block h-full bg-white rounded-full"
              style={{ width: `${i < index ? 100 : i === index ? prog * 100 : 0}%` }}
            />
          </button>
        ))}
      </div>

      {/* play overlay (stops propagation so it stays usable even when the video surface is a link) */}
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/25">
          <button
            data-testid="player-center-play"
            onClick={(e) => { e.stopPropagation(); togglePlay(); }}
            className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 glow-coral"
          >
            <Play className="h-7 w-7 text-black ml-1" fill="black" />
          </button>
        </div>
      )}

      {/* affiliate disclosure overlay */}
      {aff?.disclosure_enabled && (
        <div data-testid="player-disclosure" className="absolute top-8 left-3 right-3 z-20 text-center">
          <span className="inline-block rounded-md bg-black/60 px-2.5 py-1 text-[11px] leading-tight text-white/90 backdrop-blur">
            {aff.disclosure_text}
          </span>
        </div>
      )}

      {/* CTA button overlay */}
      {ctaVisible && (
        <motion.button
          data-testid="player-cta-btn"
          initial={{ opacity: 0, y: 12, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.35 }}
          onClick={(e) => { e.stopPropagation(); openLink("cta"); }}
          style={{ backgroundColor: cta.bg_color || "#FF453A", color: cta.text_color || "#fff" }}
          className={`absolute ${ctaPos} ${ctaSize} z-30 rounded-full font-display font-extrabold shadow-lg shadow-black/40 flex items-center gap-1.5 hover:brightness-110 transition-all`}
        >
          {cta.text || aff.cta_text || "Shop Now"} <ExternalLink className="h-3.5 w-3.5" />
        </motion.button>
      )}

      {/* designated clickable area (small product tag) */}
      {aff?.clickable_mode === "area" && aff.product_name && (
        <button
          data-testid="player-area-link"
          onClick={(e) => { e.stopPropagation(); openLink("video"); }}
          className="absolute bottom-4 left-3 z-30 rounded-xl bg-black/60 px-3 py-2 text-left backdrop-blur hover:bg-black/75 transition-colors"
        >
          <span className="block text-[10px] uppercase tracking-wide text-white/60">Featured</span>
          <span className="block text-xs font-semibold text-white">{aff.product_name}</span>
        </button>
      )}

      <audio ref={audioRef} />
    </div>
  );

  const isWide = aspect === "16:9";
  const frameW = isWide ? "w-[340px] sm:w-[460px]" : "w-[300px] sm:w-[330px]";
  const aspectCls = aspect === "1:1" ? "aspect-square" : aspect === "16:9" ? "aspect-video" : "aspect-[9/16]";
  return (
    <div className="flex flex-col items-center gap-4">
      <div className={phone && !isWide ? `phone-frame ${frameW}` : frameW}>
        <div className={`${aspectCls} w-full overflow-hidden ${isWide ? "rounded-xl" : ""}`}>{Inner}</div>
      </div>
      <div className="flex items-center gap-3">
        <button
          data-testid="player-play-btn"
          onClick={togglePlay}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-[#FF453A] hover:bg-[#FF5D54] transition-colors"
        >
          {playing ? <Pause className="h-5 w-5 text-white" /> : <Play className="h-5 w-5 text-white ml-0.5" fill="white" />}
        </button>
        <button
          data-testid="player-restart-btn"
          onClick={restart}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1E2333] hover:bg-[#2a3145] transition-colors"
        >
          <RotateCcw className="h-5 w-5 text-slate-200" />
        </button>
        <button
          data-testid="player-mute-btn"
          onClick={() => { setMuted((m) => { if (audioRef.current) audioRef.current.muted = !m; return !m; }); }}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1E2333] hover:bg-[#2a3145] transition-colors"
        >
          {muted ? <VolumeX className="h-5 w-5 text-slate-200" /> : <Volume2 className="h-5 w-5 text-slate-200" />}
        </button>
      </div>
    </div>
  );
}
